import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Tooltip,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Pending,
  TrendingUp,
  Delete,
  Visibility,
  Close,
  Refresh,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person,
  CalendarToday,
  Description as DescriptionIcon,
  Category
} from '@mui/icons-material';

const statusColors = {
  new: 'warning',
  in_progress: 'info',
  treated: 'success'
};

const statusLabels = {
  new: 'Nouveau',
  in_progress: 'En cours',
  treated: 'Traité'
};

const AdminDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchRequests();
  }, []);

 const fetchRequests = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/requests/admin/requests`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setRequests(response.data.data);
  } catch (error) {
    console.error('Erreur:', error);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    showSnackbar('Erreur lors du chargement des données', 'error');
  } finally {
    setLoading(false);
  }
};

const handleStatusChange = async (id, newStatus) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${process.env.REACT_APP_API_URL}/requests/admin/requests/${id}/status`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    showSnackbar('Statut mis à jour avec succès', 'success');
    fetchRequests();
  } catch (error) {
    console.error('Erreur:', error);
    showSnackbar('Erreur lors de la mise à jour du statut', 'error');
  }
};

const handleDeleteRequest = async () => {
  if (!selectedRequest) return;
  
  try {
    const token = localStorage.getItem('token');
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/requests/admin/requests/${selectedRequest._id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    showSnackbar('Demande supprimée avec succès', 'success');
    setDeleteDialogOpen(false);
    fetchRequests();
  } catch (error) {
    console.error('Erreur:', error);
    showSnackbar('Erreur lors de la suppression', 'error');
  }

};

  const openDeleteDialog = (request) => {
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };

  const openDetailsDialog = (request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStats = () => {
    const total = requests.length;
    const newCount = requests.filter(r => r.status === 'new').length;
    const inProgress = requests.filter(r => r.status === 'in_progress').length;
    const treated = requests.filter(r => r.status === 'treated').length;
    return { total, newCount, inProgress, treated };
  };

  const stats = getStats();

  const filteredRequests = () => {
    if (tabValue === 0) return requests;
    if (tabValue === 1) return requests.filter(r => r.status === 'new');
    if (tabValue === 2) return requests.filter(r => r.status === 'in_progress');
    if (tabValue === 3) return requests.filter(r => r.status === 'treated');
    return requests;
  };

  const getServiceLabel = (serviceType) => {
    const labels = {
      consultation: 'Consultation',
      recouvrement: 'Recouvrement',
      immobilier: 'Immobilier',
      contact: 'Contact'
    };
    return labels[serviceType] || serviceType;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
            Tableau de Bord Administrateur
          </Typography>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={fetchRequests} sx={{ bgcolor: 'rgba(26,35,126,0.1)' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Demandes
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.total}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1a237e', width: 56, height: 56 }}>
                    <Assignment />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Nouvelles
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                      {stats.newCount}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 56, height: 56 }}>
                    <Pending />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      En cours
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="info.main">
                      {stats.inProgress}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                    <TrendingUp />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Traitées
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {stats.treated}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#4caf50', width: 56, height: 56 }}>
                    <CheckCircle />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab label="Toutes" />
            <Tab label={
              <Badge badgeContent={stats.newCount} color="warning">
                Nouvelles
              </Badge>
            } />
            <Tab label="En cours" />
            <Tab label="Traitées" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests().map((request) => (
                  <TableRow key={request._id} sx={{ '&:hover': { backgroundColor: 'rgba(26,35,126,0.02)' } }}>
                    <TableCell>
                      {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {request.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          {request.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          {request.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getServiceLabel(request.serviceType)} 
                        size="small" 
                        color="primary"
                        sx={{ borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {request.description?.substring(0, 80)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={request.status}
                        onChange={(e) => handleStatusChange(request._id, e.target.value)}
                        sx={{ minWidth: 130, borderRadius: 2 }}
                      >
                        {Object.entries(statusLabels).map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            <Chip 
                              label={label} 
                              size="small" 
                              color={statusColors[value]}
                              sx={{ borderRadius: 2 }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir les détails">
                        <IconButton 
                          size="small" 
                          onClick={() => openDetailsDialog(request)}
                          sx={{ color: '#1976d2', mr: 1 }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          size="small" 
                          onClick={() => openDeleteDialog(request)}
                          sx={{ color: '#d32f2f' }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Dialog de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white' }}>
          <Box display="flex" alignItems="center">
            <Delete sx={{ mr: 1 }} />
            Confirmer la suppression
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la demande de <strong>{selectedRequest?.fullName}</strong> ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Annuler
          </Button>
          <Button onClick={handleDeleteRequest} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog des détails */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <DescriptionIcon sx={{ mr: 1 }} />
              Détails de la demande
            </Box>
            <IconButton size="small" onClick={() => setDetailsDialogOpen(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedRequest && (
            <Box>
              {/* En-tête avec statut */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Chip 
                  label={statusLabels[selectedRequest.status]} 
                  color={statusColors[selectedRequest.status]}
                  sx={{ borderRadius: 2, fontSize: '0.9rem', p: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  ID: {selectedRequest._id}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Informations client */}
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: 1 }} /> Informations client
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Nom complet</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedRequest.fullName}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1">
                      <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {selectedRequest.email}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                    <Typography variant="body1">
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {selectedRequest.phone}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Date de création</Typography>
                    <Typography variant="body1">
                      <CalendarToday sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      {new Date(selectedRequest.createdAt).toLocaleString('fr-FR')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Détails du service */}
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <Category sx={{ mr: 1 }} /> Détails du service
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Type de service</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {getServiceLabel(selectedRequest.serviceType)}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedRequest.legalDomain && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Domaine juridique</Typography>
                      <Typography variant="body1">{selectedRequest.legalDomain}</Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedRequest.amount && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Montant</Typography>
                      <Typography variant="body1">{selectedRequest.amount} {selectedRequest.currency || 'XOF'}</Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedRequest.propertyType && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Type de bien</Typography>
                      <Typography variant="body1">{selectedRequest.propertyType}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} /> Description
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f7fa' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRequest.description || 'Aucune description fournie'}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)} variant="contained" color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;